/**
 * Book Detail — every Stripe purchase against this book.
 *
 * Row counts here are tiny (a tier, maybe an upgrade, maybe extras), so this
 * uses the shared common/DataTable rather than a DataGrid.
 */

import React from 'react';
import { Card, CardContent, Typography, Chip, Box } from '@mui/material';
import DataTable, { Column } from '../../common/DataTable';
import { centsToUsd, shortDate, TIER_META } from '../weddingFormat';
import type { Purchase, Tier } from '../weddingApi';

const PURCHASE_TYPE_LABEL: Record<string, string> = {
  tier: 'Book',
  upgrade: 'Tier upgrade',
  extra_contributors: 'Extra contributors',
};

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  paid: 'success',
  pending: 'warning',
  refunded: 'default',
  failed: 'error',
};

const PurchasesCard: React.FC<{ purchases: Purchase[]; revenueCents: number }> = ({
  purchases,
  revenueCents,
}) => {
  const columns: Column<Purchase>[] = [
    {
      id: 'purchase_type',
      label: 'What',
      render: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {PURCHASE_TYPE_LABEL[row.purchase_type] || row.purchase_type}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.tier ? TIER_META[row.tier as Tier]?.label || row.tier : ''}
            {row.quantity > 1 ? ` × ${row.quantity}` : ''}
            {row.coupon_code ? ` · ${row.coupon_code}` : ''}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'amount_cents',
      label: 'Amount',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {centsToUsd(row.amount_cents)}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => (
        <Chip label={row.status} size="small" color={STATUS_COLOR[row.status] || 'default'} />
      ),
    },
    {
      id: 'purchased_at',
      label: 'When',
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {shortDate(row.purchased_at || row.created_at)}
        </Typography>
      ),
    },
  ];

  return (
    <Card sx={{ borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Purchases
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {centsToUsd(revenueCents)} collected across {purchases.length} transaction
          {purchases.length === 1 ? '' : 's'}.
        </Typography>
        <DataTable
          columns={columns}
          data={purchases}
          emptyMessage="No purchase has been recorded against this book."
        />
      </CardContent>
    </Card>
  );
};

export default PurchasesCard;
